import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CanvasPrimitiveType } from "@ltypes/scene";
import {
    EditorPrimitive,
    boundingBox,
    movePrimitive,
    round,
    DEVICE_WIDTH,
    DEVICE_HEIGHT,
    GLYPH_W,
    GLYPH_H,
} from "./primitives";
import { CanvasVariableContext, resolveVariables } from "./variables";

const HANDLE_PX = 9; // on-screen handle size, kept constant regardless of zoom

type Handle = {
    key: string,
    x: number,
    y: number,
    apply: (devX: number, devY: number) => EditorPrimitive,
};

const hypot = (dx: number, dy: number) => Math.sqrt(dx * dx + dy * dy);

// Angle convention matching the firmware's TFT_eSPI::drawSmoothArc:
// 0deg = 6 o'clock (bottom), 90deg = 9 o'clock (left), 180deg = top,
// 270deg = 3 o'clock (right); increasing clockwise.
const polar = (cx: number, cy: number, r: number, deg: number) => {
    const a = (deg * Math.PI) / 180;
    return { x: cx - r * Math.sin(a), y: cy + r * Math.cos(a) };
};
const angleOf = (cx: number, cy: number, px: number, py: number) => {
    let deg = (Math.atan2(-(px - cx), py - cy) * 180) / Math.PI;
    if (deg < 0) deg += 360;
    return Math.round(deg);
};

function arcPath(cx: number, cy: number, r: number, start: number, end: number): string {
    let sweep = end - start;
    if (sweep <= 0) sweep += 360;
    if (sweep >= 360) {
        // Full ring drawn as two half arcs.
        const a = polar(cx, cy, r, 0);
        const b = polar(cx, cy, r, 180);
        return `M ${a.x} ${a.y} A ${r} ${r} 0 1 1 ${b.x} ${b.y} A ${r} ${r} 0 1 1 ${a.x} ${a.y}`;
    }
    const s = polar(cx, cy, r, start);
    const e = polar(cx, cy, r, end);
    const largeArc = sweep > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 1 ${e.x} ${e.y}`;
}

function getHandles(p: EditorPrimitive): Array<Handle> {
    switch (p.type) {
        case 'rectangle': {
            const left = p.position.x, top = p.position.y, right = p.position.x + p.width, bottom = p.position.y + p.height;
            const fromEdges = (l: number, t: number, r: number, b: number): EditorPrimitive => {
                const nl = Math.min(l, r), nr = Math.max(l, r), nt = Math.min(t, b), nb = Math.max(t, b);
                return { ...p, position: { x: round(nl), y: round(nt) }, width: round(Math.max(1, nr - nl)), height: round(Math.max(1, nb - nt)) };
            };
            return [
                { key: 'nw', x: left, y: top, apply: (dx, dy) => fromEdges(dx, dy, right, bottom) },
                { key: 'n', x: (left + right) / 2, y: top, apply: (_dx, dy) => fromEdges(left, dy, right, bottom) },
                { key: 'ne', x: right, y: top, apply: (dx, dy) => fromEdges(left, dy, dx, bottom) },
                { key: 'e', x: right, y: (top + bottom) / 2, apply: (dx) => fromEdges(left, top, dx, bottom) },
                { key: 'se', x: right, y: bottom, apply: (dx, dy) => fromEdges(left, top, dx, dy) },
                { key: 's', x: (left + right) / 2, y: bottom, apply: (_dx, dy) => fromEdges(left, top, right, dy) },
                { key: 'sw', x: left, y: bottom, apply: (dx, dy) => fromEdges(dx, top, right, dy) },
                { key: 'w', x: left, y: (top + bottom) / 2, apply: (dx) => fromEdges(dx, top, right, bottom) },
            ];
        }
        case 'circle': {
            const c = p.position;
            const corner = (deg: number) => polar(c.x, c.y, p.radius, deg);
            const mk = (key: string, deg: number): Handle => ({
                key, x: corner(deg).x, y: corner(deg).y,
                apply: (dx, dy) => ({ ...p, radius: round(Math.max(1, hypot(dx - c.x, dy - c.y))) }),
            });
            return [mk('ne', 45), mk('se', 135), mk('sw', 225), mk('nw', 315)];
        }
        case 'arc': {
            const c = p.position;
            const radiusHandle = polar(c.x, c.y, p.radius, 135);
            const startH = polar(c.x, c.y, p.radius, p.start_angle);
            const endH = polar(c.x, c.y, p.radius, p.end_angle);
            return [
                { key: 'radius', x: radiusHandle.x, y: radiusHandle.y, apply: (dx, dy) => ({ ...p, radius: round(Math.max(1, hypot(dx - c.x, dy - c.y))) }) },
                { key: 'start', x: startH.x, y: startH.y, apply: (dx, dy) => ({ ...p, start_angle: angleOf(c.x, c.y, dx, dy) }) },
                { key: 'end', x: endH.x, y: endH.y, apply: (dx, dy) => ({ ...p, end_angle: angleOf(c.x, c.y, dx, dy) }) },
            ];
        }
        case 'text': {
            const bb = boundingBox(p);
            return [
                { key: 'se', x: bb.x + bb.w, y: bb.y + bb.h, apply: (_dx, dy) => ({ ...p, size: Math.max(1, Math.min(7, Math.round((dy - p.position.y) / GLYPH_H))) }) },
            ];
        }
        case 'line':
            return [
                { key: 'a', x: p.pointA.x, y: p.pointA.y, apply: (dx, dy) => ({ ...p, pointA: { x: round(dx), y: round(dy) } }) },
                { key: 'b', x: p.pointB.x, y: p.pointB.y, apply: (dx, dy) => ({ ...p, pointB: { x: round(dx), y: round(dy) } }) },
            ];
        case 'triangle':
            return [
                { key: 'a', x: p.pointA.x, y: p.pointA.y, apply: (dx, dy) => ({ ...p, pointA: { x: round(dx), y: round(dy) } }) },
                { key: 'b', x: p.pointB.x, y: p.pointB.y, apply: (dx, dy) => ({ ...p, pointB: { x: round(dx), y: round(dy) } }) },
                { key: 'c', x: p.pointC.x, y: p.pointC.y, apply: (dx, dy) => ({ ...p, pointC: { x: round(dx), y: round(dy) } }) },
            ];
        default:
            return [];
    }
}

function renderShape(p: EditorPrimitive, ctx: CanvasVariableContext): React.ReactNode {
    switch (p.type) {
        case 'rectangle':
            return <rect x={p.position.x} y={p.position.y} width={Math.max(0, p.width)} height={Math.max(0, p.height)} rx={p.radius || 0} ry={p.radius || 0} fill={p.color} />;
        case 'circle':
            return <circle cx={p.position.x} cy={p.position.y} r={Math.max(0, p.radius)} fill={p.color} />;
        case 'arc':
            return <path d={arcPath(p.position.x, p.position.y, Math.max(0, p.radius - p.thickness / 2), p.start_angle, p.end_angle)} fill="none" stroke={p.color} strokeWidth={p.thickness} />;
        case 'pixel':
            return <rect x={p.position.x} y={p.position.y} width={1} height={1} fill={p.color} />;
        case 'line':
            return <line x1={p.pointA.x} y1={p.pointA.y} x2={p.pointB.x} y2={p.pointB.y} stroke={p.color} strokeWidth={Math.max(1, p.thickness)} strokeLinecap="round" />;
        case 'triangle':
            return <polygon points={`${p.pointA.x},${p.pointA.y} ${p.pointB.x},${p.pointB.y} ${p.pointC.x},${p.pointC.y}`} fill={p.color} />;
        case 'text': {
            const value = resolveVariables(p.text, ctx) || ' ';
            return (
                <text
                    x={p.position.x}
                    y={p.position.y}
                    fill={p.color}
                    fontFamily="'Courier New', monospace"
                    fontSize={GLYPH_H * p.size}
                    textLength={Math.max(1, value.length * GLYPH_W * p.size)}
                    lengthAdjust="spacingAndGlyphs"
                    dominantBaseline="text-before-edge"
                    style={{ whiteSpace: 'pre' }}
                >
                    {value}
                </text>
            );
        }
        default:
            return null;
    }
}

type CanvasStageProps = {
    primitives: Array<EditorPrimitive>,
    selectedId: string | null,
    ctx: CanvasVariableContext,
    showGrid: boolean,
    onSelect: (id: string | null) => void,
    onUpdate: (id: string, p: EditorPrimitive) => void,
    onCommit: () => void,
    onCreateAt: (type: CanvasPrimitiveType, x: number, y: number) => void,
};

function CanvasStage(props: CanvasStageProps) {
    const svgRef = useRef<SVGSVGElement>(null);
    const [scale, setScale] = useState<number>(1);
    const [dragOver, setDragOver] = useState<boolean>(false);

    const dragRef = useRef<{ id: string, kind: string, orig: EditorPrimitive, startX: number, startY: number, moved: boolean } | null>(null);

    useLayoutEffect(() => {
        const measure = () => {
            if (svgRef.current) setScale(svgRef.current.getBoundingClientRect().width / DEVICE_WIDTH || 1);
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (svgRef.current) ro.observe(svgRef.current);
        return () => ro.disconnect();
    }, []);

    const toDevice = (clientX: number, clientY: number) => {
        const rect = svgRef.current!.getBoundingClientRect();
        return {
            x: ((clientX - rect.left) / rect.width) * DEVICE_WIDTH,
            y: ((clientY - rect.top) / rect.height) * DEVICE_HEIGHT,
        };
    };

    const hitInfo = (target: EventTarget | null): { id?: string, handle?: string } => {
        let el = target as Element | null;
        while (el && el !== svgRef.current) {
            const ds = (el as HTMLElement).dataset;
            if (ds && (ds.id || ds.handle)) return { id: ds.id, handle: ds.handle };
            el = el.parentElement;
        }
        return {};
    };

    const onPointerDown = (e: React.PointerEvent) => {
        const hit = hitInfo(e.target);
        const dev = toDevice(e.clientX, e.clientY);

        if (hit.handle && props.selectedId) {
            const orig = props.primitives.find((p) => p._id === props.selectedId);
            if (!orig) return;
            dragRef.current = { id: props.selectedId, kind: `handle:${hit.handle}`, orig, startX: dev.x, startY: dev.y, moved: false };
            (e.target as Element).setPointerCapture?.(e.pointerId);
            e.preventDefault();
            return;
        }

        if (hit.id) {
            const orig = props.primitives.find((p) => p._id === hit.id);
            if (!orig) return;
            if (props.selectedId !== hit.id) props.onSelect(hit.id);
            dragRef.current = { id: hit.id, kind: 'move', orig, startX: dev.x, startY: dev.y, moved: false };
            svgRef.current?.setPointerCapture(e.pointerId);
            e.preventDefault();
            return;
        }

        // Clicked empty space -> deselect.
        props.onSelect(null);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dev = toDevice(e.clientX, e.clientY);
        drag.moved = true;

        if (drag.kind === 'move') {
            props.onUpdate(drag.id, movePrimitive(drag.orig, dev.x - drag.startX, dev.y - drag.startY));
        } else if (drag.kind.startsWith('handle:')) {
            const key = drag.kind.slice('handle:'.length);
            const handle = getHandles(drag.orig).find((h) => h.key === key);
            if (handle) props.onUpdate(drag.id, handle.apply(dev.x, dev.y));
        }
    };

    const onPointerUp = () => {
        if (dragRef.current && dragRef.current.moved) props.onCommit();
        dragRef.current = null;
    };

    // Native HTML5 drop from the toolbox palette.
    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const type = e.dataTransfer.getData('application/x-lumeba-primitive') as CanvasPrimitiveType;
        if (!type) return;
        const dev = toDevice(e.clientX, e.clientY);
        props.onCreateAt(type, dev.x, dev.y);
    };

    const hsize = HANDLE_PX / scale;
    const selected = props.primitives.find((p) => p._id === props.selectedId) || null;
    const selBox = selected ? boundingBox(selected) : null;

    // Approx stroke width for selection chrome, constant on screen.
    const sw = 1.2 / scale;

    return (
        <div className={`canvas-stage ${dragOver ? 'drag-over' : ''}`}>
            <svg
                ref={svgRef}
                className="canvas-stage-svg"
                viewBox={`0 0 ${DEVICE_WIDTH} ${DEVICE_HEIGHT}`}
                preserveAspectRatio="xMidYMid meet"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
            >
                {/* Device background (firmware clears to black). */}
                <rect x={0} y={0} width={DEVICE_WIDTH} height={DEVICE_HEIGHT} fill="#000000" />

                {props.showGrid && (
                    <g stroke="#8a93a6" strokeOpacity={0.4} strokeWidth={1} vectorEffect="non-scaling-stroke" shapeRendering="crispEdges">
                        {Array.from({ length: Math.floor(DEVICE_WIDTH / 20) }, (_, i) => (
                            <line key={`v${i}`} x1={(i + 1) * 20} y1={0} x2={(i + 1) * 20} y2={DEVICE_HEIGHT} />
                        ))}
                        {Array.from({ length: Math.floor(DEVICE_HEIGHT / 20) }, (_, i) => (
                            <line key={`h${i}`} x1={0} y1={(i + 1) * 20} x2={DEVICE_WIDTH} y2={(i + 1) * 20} />
                        ))}
                    </g>
                )}

                {/* Primitives painted in array order (later = on top), like the firmware. */}
                {props.primitives.map((p) => {
                    const bb = boundingBox(p);
                    return (
                        <g key={p._id} data-id={p._id} style={{ cursor: 'move' }}>
                            {renderShape(p, props.ctx)}
                            {/* Transparent hit area so thin/small shapes stay grabbable. */}
                            <rect
                                x={bb.x - 5 / scale}
                                y={bb.y - 5 / scale}
                                width={bb.w + 10 / scale}
                                height={bb.h + 10 / scale}
                                fill="transparent"
                            />
                            {p.type === 'line' && (
                                <line x1={p.pointA.x} y1={p.pointA.y} x2={p.pointB.x} y2={p.pointB.y} stroke="transparent" strokeWidth={Math.max(p.thickness, 12 / scale)} />
                            )}
                        </g>
                    );
                })}

                {/* Selection chrome */}
                {selected && selBox && (
                    <g pointerEvents="none">
                        <rect
                            x={selBox.x - 1 / scale}
                            y={selBox.y - 1 / scale}
                            width={selBox.w + 2 / scale}
                            height={selBox.h + 2 / scale}
                            fill="none"
                            stroke="#346fed"
                            strokeWidth={sw}
                            strokeDasharray={`${4 / scale} ${3 / scale}`}
                        />
                    </g>
                )}

                {/* Handles (interactive) */}
                {selected && getHandles(selected).map((h) => (
                    <rect
                        key={h.key}
                        data-handle={h.key}
                        x={h.x - hsize / 2}
                        y={h.y - hsize / 2}
                        width={hsize}
                        height={hsize}
                        rx={hsize * 0.18}
                        fill="#ffffff"
                        stroke="#346fed"
                        strokeWidth={sw}
                        style={{ cursor: 'pointer' }}
                    />
                ))}
            </svg>
        </div>
    );
}

export default CanvasStage;
